import { useMutation } from "convex/react"
import { useCallback, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAppAuth } from "@/hooks/useAppAuth"
import { api } from "~convex/_generated/api"
import type { Id } from "~convex/_generated/dataModel"
import { Avatar } from "./Avatar"
import { LinkifiedText } from "./LinkifiedText"
import { PayModal } from "./PayModal"

interface UserProfileHeaderProps {
	avatarUrl?: string
	bio?: string
	displayName: string
	followerCount: number
	followingCount: number
	followsYou?: boolean
	handle: string
	isFollowedByMe?: boolean
	isMirrored?: boolean
	postCount: number
	twitterUsername?: string
	userId: Id<"users">
	walletAddress?: string
}

export function UserProfileHeader({
	avatarUrl,
	bio,
	displayName,
	followerCount,
	followingCount,
	followsYou,
	handle,
	isFollowedByMe,
	isMirrored,
	postCount,
	twitterUsername,
	userId,
	walletAddress,
}: UserProfileHeaderProps) {
	const { isAuthenticated, currentUser } = useAppAuth()
	const navigate = useNavigate()
	const toggleFollow = useMutation(api.users.toggleFollow)
	const isOwnProfile = currentUser?._id === userId
	const [payModalOpen, setPayModalOpen] = useState(false)

	const handleFollow = useCallback(() => {
		if (!isAuthenticated) {
			Promise.resolve(navigate("/login")).catch(() => {})
			return
		}
		toggleFollow({ target_user_id: userId }).catch(() => toast.error("Failed to follow user"))
	}, [isAuthenticated, navigate, toggleFollow, userId])

	return (
		<div className="border-app-border border-b px-3 py-3 sm:px-4 sm:py-4">
			<div className="flex items-start justify-between">
				<Avatar
					alt={handle}
					fallback={(displayName || handle)[0]?.toUpperCase() ?? ""}
					size="xl"
					url={avatarUrl}
				/>

				{isOwnProfile ? (
					<Link
						className="rounded-full border border-app-border px-5 py-1.5 font-semibold text-app-text text-sm transition-opacity hover:opacity-80"
						to="/profile/edit"
					>
						Edit Profile
					</Link>
				) : (
					<div className="flex items-center gap-2">
						{isAuthenticated && (
							<button
								className="rounded-full border border-app-green px-5 py-1.5 font-semibold text-app-green text-sm transition-opacity hover:opacity-80"
								onClick={() => setPayModalOpen(true)}
								type="button"
							>
								Pay
							</button>
						)}
						<button
							className={`rounded-full px-5 py-1.5 font-semibold text-sm transition-opacity hover:opacity-90 ${
								isFollowedByMe
									? "border border-app-border bg-transparent text-app-text"
									: "bg-white text-black"
							}`}
							onClick={handleFollow}
							type="button"
						>
							{isFollowedByMe ? "Following" : "Follow"}
						</button>
					</div>
				)}
			</div>

			<div className="mt-3">
				<h1 className="flex items-center gap-1.5 font-bold text-app-text text-xl">
					{displayName || handle}
					{isMirrored && <span title="Mirrored account">{"\u{1F916}"}</span>}
				</h1>
				<p className="text-app-text-secondary text-sm">
					@{handle}
					{followsYou && (
						<span className="ml-2 rounded bg-app-surface-light px-1.5 py-0.5 text-app-text-tertiary text-xs">
							Follows you
						</span>
					)}
				</p>
			</div>

			{bio && (
				<LinkifiedText className="mt-2 block text-app-text text-sm leading-relaxed" text={bio} />
			)}

			{walletAddress && (
				<button
					className="mt-2 flex items-center gap-1.5 font-mono text-app-text-secondary text-xs transition-colors hover:text-app-text"
					onClick={() => {
						navigator.clipboard.writeText(walletAddress).catch(() => {})
						toast.success("Address copied")
					}}
					title={walletAddress}
					type="button"
				>
					{walletAddress.slice(0, 4)}...{walletAddress.slice(-4)}
					<svg
						className="h-3 w-3"
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
						viewBox="0 0 24 24"
					>
						<rect height="13" rx="2" width="13" x="9" y="9" />
						<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
					</svg>
				</button>
			)}

			<div className="mt-3 flex gap-4 text-sm">
				<span>
					<span className="font-semibold text-app-text">{postCount}</span>{" "}
					<span className="text-app-text-secondary">{postCount === 1 ? "Post" : "Posts"}</span>
				</span>
				<Link className="hover:underline" to={`/user/${handle}/following`}>
					<span className="font-semibold text-app-text">{followingCount}</span>{" "}
					<span className="text-app-text-secondary">Following</span>
				</Link>
				<Link className="hover:underline" to={`/user/${handle}/followers`}>
					<span className="font-semibold text-app-text">{followerCount}</span>{" "}
					<span className="text-app-text-secondary">
						{followerCount === 1 ? "Follower" : "Followers"}
					</span>
				</Link>
			</div>

			{twitterUsername && (
				<a
					className="mt-2 inline-block text-app-text-secondary text-sm hover:text-app-text"
					href={`https://x.com/${twitterUsername}`}
					rel="noopener noreferrer"
					target="_blank"
				>
					@{twitterUsername} on X
				</a>
			)}

			{payModalOpen && (
				<PayModal
					onClose={() => setPayModalOpen(false)}
					recipientDisplayName={displayName}
					recipientHandle={handle}
					recipientUserId={userId}
				/>
			)}
		</div>
	)
}
